import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  User,
  Mail,
  Phone,
  Lock,
  Save,
  Eye,
  EyeOff,
  Link as LinkIcon,
  Edit2,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { addNotification } from "../../store/slices/uiSlice";
import { authAPI } from "../../services/api";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import Toast from "../../components/UI/Toast";
import { logError } from "../../utils/errorLogger";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { PhoneInputWithValidation, PhoneValidationResponse } from "../../components/UI/PhoneInputWithValidation";
import { parseE164Phone } from "../../utils/phoneValidationV2";
import { findCountryByCode } from "../../data/countryCodes";

interface ProfileFormData {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  preferred_language: string;
  contact_mirsal_link?: string;
}

interface PasswordFormData {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
}

const ProfilePage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<"profile" | "password">("profile");
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [isEditingContactLink, setIsEditingContactLink] = useState(false);
  const [sharedContactLink, setSharedContactLink] = useState<string>("");
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  const [profileCountryCode, setProfileCountryCode] = useState<string>("JO");

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    watch: watchProfile,
    setValue: setProfileValue,
  } = useForm<ProfileFormData>({
    defaultValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      preferred_language: user?.preferred_language || "en",
      contact_mirsal_link: (user as any)?.contact_mirsal_link || "",
    },
  });


  const contactLinkValue = watchProfile("contact_mirsal_link");

  const fetchSharedContactLink = async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.data.success && response.data.data) {
        // Update all profile fields
        setProfileValue("first_name", response.data.data.first_name || "");
        setProfileValue("last_name", response.data.data.last_name || "");
        setProfileValue("email", response.data.data.email || "");
        const phone = response.data.data.phone || "";
        if (phone) {
          try {
            const { localNumber, countryCode } = parseE164Phone(phone);
            setProfileValue("phone", localNumber || phone);
            setProfileCountryCode(countryCode || "JO");
          } catch {
            setProfileValue("phone", phone);
            setProfileCountryCode("JO");
          }
        } else {
          setProfileValue("phone", "");
          setProfileCountryCode("JO");
        }
        setProfileValue(
          "preferred_language",
          response.data.data.preferred_language || "en"
        );

        // Get the contact link (either user's own or shared from any admin)
        const contactLink =
          response.data.data.contact_mirsal_link ||
          response.data.data.shared_contact_mirsal_link;
        if (contactLink) {
          setSharedContactLink(contactLink);
          setProfileValue("contact_mirsal_link", contactLink);
        }
      }
    } catch (error) {
      logError("Failed to fetch contact link", error);
    }
  };

  // Load profile data when component mounts
  useEffect(() => {
    const loadProfile = async () => {
      if (user?.role === "admin") {
        await fetchSharedContactLink();
      } else {
        // For non-admin users, just load their profile
        try {
          const response = await authAPI.getProfile();
          if (response.data.success && response.data.data) {
            setProfileValue("first_name", response.data.data.first_name || "");
            setProfileValue("last_name", response.data.data.last_name || "");
            setProfileValue("email", response.data.data.email || "");
            
            // Parse phone number to extract local number and country for the selector
            const phone = response.data.data.phone || "";
            if (phone) {
              try {
                const { localNumber, countryCode } = parseE164Phone(phone);
                setProfileValue("phone", localNumber || phone);
                setProfileCountryCode(countryCode || "JO");
              } catch (error) {
                setProfileValue("phone", phone);
                setProfileCountryCode("JO");
              }
            } else {
              setProfileValue("phone", "");
              setProfileCountryCode("JO");
            }
            
            setProfileValue(
              "preferred_language",
              response.data.data.preferred_language || "en"
            );
          }
        } catch (error) {
          logError("Failed to load profile", error);
        }
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Update form when shared contact link is fetched
  useEffect(() => {
    if (sharedContactLink) {
      // Always use shared link if available (it will be the user's own if they set it, or from another admin)
      setProfileValue("contact_mirsal_link", sharedContactLink);
    }
  }, [sharedContactLink, setProfileValue]);

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>();

  const newPassword = watch("new_password");

  // Password strength calculation
  const getPasswordStrength = (
    password: string
  ): { score: number; label: string; color: string } => {
    if (!password) return { score: 0, label: "", color: "" };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    Object.values(checks).forEach((check) => check && score++);

    if (score <= 2) return { score, label: "Weak", color: "bg-red-500" };
    if (score <= 3) return { score, label: "Fair", color: "bg-yellow-500" };
    if (score <= 4) return { score, label: "Good", color: "bg-blue-500" };
    return { score, label: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(newPassword || "");

  // Auto-clear toast after 5 seconds
  useAutoDismiss(toast, () => setToast(null), 5000);

  const onProfileSubmit = async (data: ProfileFormData) => {
    setLoading(true);
    
    // Validate and use normalized phone number
    if (data.phone && data.phone.trim()) {
      if (!phoneValidation || !phoneValidation.valid) {
        setToast({ type: "error", message: "Please enter a valid phone number" });
        setLoading(false);
        return;
      }
      
      // Use normalized phone (E.164 format) from validation
      data.phone = phoneValidation.normalized;
    }
    
    try {
      const response = await authAPI.updateProfile({
        ...data,
        country_code: phoneValidation?.country || 'JO'
      });

      if (response.data.success) {
        // If contact link was updated, refresh the shared link
        if (data.contact_mirsal_link) {
          setSharedContactLink(data.contact_mirsal_link);
          setIsEditingContactLink(false);
        }
        dispatch(
          addNotification({
            type: "success",
            title: "Profile Updated",
            message: "Your profile has been updated successfully.",
            duration: 5000,
          })
        );
        setToast({ type: "success", message: "Profile updated successfully!" });
      } else {
        throw new Error(
          response.data.error?.message || "Failed to update profile"
        );
      }
    } catch (error: any) {
      logError("Profile update failed", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to update profile. Please try again.";

      dispatch(
        addNotification({
          type: "error",
          title: "Update Failed",
          message: errorMessage,
          duration: 7000,
        })
      );
      setToast({ type: "error", message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    setLoading(true);
    try {
      const response = await authAPI.changePassword(data);

      if (response.data.success) {
        dispatch(
          addNotification({
            type: "success",
            title: "Password Changed",
            message: "Your password has been changed successfully.",
            duration: 5000,
          })
        );
        setToast({
          type: "success",
          message: "Password changed successfully!",
        });
        resetPassword();
      } else {
        throw new Error(
          response.data.error?.message || "Failed to change password"
        );
      }
    } catch (error: any) {
      logError("Password change failed", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to change password. Please try again.";

      dispatch(
        addNotification({
          type: "error",
          title: "Password Change Failed",
          message: errorMessage,
          duration: 7000,
        })
      );
      setToast({ type: "error", message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-gray-900" : "bg-gray-50"
      } transition-colors duration-200`}
    >
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Page Header */}
        <div>
          <h1
            className={`text-3xl font-bold ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}
          >
            Profile Settings
          </h1>
          <p
            className={`mt-2 text-sm ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Manage your account information and security settings
          </p>
        </div>

        {/* User Info Card */}
        <div
          className={`relative overflow-hidden rounded-2xl shadow-xl border backdrop-blur-sm ${
            theme === "dark"
              ? "bg-gray-800/90 border-gray-700/50"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Background gradient */}
          <div
            className={`absolute inset-0 ${
              theme === "dark"
                ? "bg-gradient-to-br from-gray-800 via-gray-800/95 to-gray-900"
                : "bg-gradient-to-br from-white via-gray-50/50 to-white"
            }`}
          ></div>

          <div className="relative z-10 p-6">
            <div className="flex items-center space-x-4">
              <div
                className={`h-20 w-20 rounded-full flex items-center justify-center shadow-lg ${
                  theme === "dark"
                    ? "bg-gradient-to-br from-[#33FF00]/20 to-[#2BD400]/20 border border-[#33FF00]/30"
                    : "bg-gradient-to-br from-[#33FF00]/10 to-[#2BD400]/10 border border-[#33FF00]/20"
                }`}
              >
                <span
                  className={`text-2xl font-bold ${
                    theme === "dark" ? "text-[#33FF00]" : "text-[#2BD400]"
                  }`}
                >
                  {user?.first_name?.[0] || "U"}
                  {user?.last_name?.[0] || ""}
                </span>
              </div>
              <div className="flex-1">
                <h3
                  className={`text-xl font-semibold ${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {user?.first_name} {user?.last_name}
                </h3>
                <p
                  className={`text-sm mt-1 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {user?.email || user?.phone}
                </p>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      user?.role === "admin"
                        ? theme === "dark"
                          ? "bg-red-900/30 text-red-300 border border-red-700/50"
                          : "bg-red-100 text-red-800"
                        : user?.role === "host"
                        ? theme === "dark"
                          ? "bg-blue-900/30 text-blue-300 border border-blue-700/50"
                          : "bg-blue-100 text-blue-800"
                        : theme === "dark"
                        ? "bg-gray-700 text-gray-300 border border-gray-600"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {user?.role?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`relative overflow-hidden rounded-2xl shadow-xl border backdrop-blur-sm ${
            theme === "dark"
              ? "bg-gray-800/90 border-gray-700/50"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Background gradient */}
          <div
            className={`absolute inset-0 ${
              theme === "dark"
                ? "bg-gradient-to-br from-gray-800 via-gray-800/95 to-gray-900"
                : "bg-gradient-to-br from-white via-gray-50/50 to-white"
            }`}
          ></div>

          <div className="relative z-10">
            <div
              className={`border-b ${
                theme === "dark" ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <nav className="-mb-px flex overflow-x-auto space-x-6 sm:space-x-8 px-4 sm:px-6">
                <button
                  type="button"
                  onClick={() => setActiveTab("profile")}
                  className={`flex-shrink-0 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "profile"
                      ? theme === "dark"
                        ? "border-[#33FF00] text-[#33FF00]"
                        : "border-[#2BD400] text-[#2BD400]"
                      : theme === "dark"
                      ? "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Profile Information
                </button>
                {user?.role !== "guest" && (
                  <button
                    type="button"
                    onClick={() => setActiveTab("password")}
                    className={`flex-shrink-0 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === "password"
                        ? theme === "dark"
                          ? "border-[#33FF00] text-[#33FF00]"
                          : "border-[#2BD400] text-[#2BD400]"
                        : theme === "dark"
                        ? "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Password & Security
                  </button>
                )}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === "profile" && (
                <form
                  onSubmit={handleProfileSubmit(onProfileSubmit)}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                      label="First Name"
                      leftIcon={<User className="h-5 w-5" />}
                      error={profileErrors.first_name?.message}
                      {...registerProfile("first_name", {
                        required: "First name is required",
                        minLength: {
                          value: 2,
                          message: "First name must be at least 2 characters",
                        },
                      })}
                    />

                    <Input
                      label="Last Name"
                      leftIcon={<User className="h-5 w-5" />}
                      error={profileErrors.last_name?.message}
                      {...registerProfile("last_name", {
                        required: "Last name is required",
                        minLength: {
                          value: 2,
                          message: "Last name must be at least 2 characters",
                        },
                      })}
                    />
                  </div>

                  {user?.email && (
                    <Input
                      label="Email Address"
                      type="email"
                      leftIcon={<Mail className="h-5 w-5" />}
                      error={profileErrors.email?.message}
                      {...registerProfile("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                    />
                  )}

                  <div>
                    <PhoneInputWithValidation
                      value={watchProfile("phone") || ""}
                      onChange={(phoneValue, countryCodeValue) => {
                        setProfileValue("phone", phoneValue);
                        setProfileCountryCode(countryCodeValue);
                      }}
                      onValidationChange={setPhoneValidation}
                      error={profileErrors.phone?.message}
                      required
                      showValidationResult={false}
                      placeholder="Enter phone number"
                      initialCountryCode={profileCountryCode}
                    />
                  </div>

                  {/* Contact Mirsal Link - Admin Only */}
                  {user?.role === "admin" && (
                    <div
                      className={`p-4 rounded-xl border ${
                        theme === "dark"
                          ? "bg-gray-900/50 border-gray-700/50"
                          : "bg-blue-50/50 border-blue-200/50"
                      }`}
                    >
                      <label
                        className={`block text-sm font-semibold mb-2 ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Contact Mirsal Link (Optional)
                      </label>
                      <div className="relative">
                        <div
                          className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
                            theme === "dark" ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          <LinkIcon className="h-5 w-5" />
                        </div>
                        {isEditingContactLink || !contactLinkValue ? (
                          <>
                            <input
                              type="url"
                              placeholder="e.g., https://wa.me/1234567890 or https://mirsal.com/contact"
                              className={`block w-full pl-10 pr-20 py-2.5 rounded-lg border shadow-sm transition-all sm:text-sm ${
                                theme === "dark"
                                  ? profileErrors.contact_mirsal_link
                                    ? "bg-gray-800 border-red-500/50 text-white placeholder-gray-500 focus:border-red-500 focus:ring-red-500/20"
                                    : "bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-[#33FF00] focus:ring-[#33FF00]/20"
                                  : profileErrors.contact_mirsal_link
                                  ? "bg-white border-red-300 text-gray-900 placeholder-gray-400 focus:border-red-500 focus:ring-red-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-[#2BD400] focus:ring-[#2BD400]/20"
                              }`}
                              {...registerProfile("contact_mirsal_link", {
                                pattern: {
                                  value: /^https?:\/\/.+/,
                                  message:
                                    "Please enter a valid URL starting with http:// or https://",
                                },
                              })}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setIsEditingContactLink(false);
                                // Reset to shared link if user clears it
                                if (!contactLinkValue && sharedContactLink) {
                                  setProfileValue(
                                    "contact_mirsal_link",
                                    sharedContactLink
                                  );
                                }
                              }}
                              className={`absolute inset-y-0 right-0 pr-3 flex items-center transition-colors ${
                                theme === "dark"
                                  ? "text-gray-400 hover:text-gray-300"
                                  : "text-gray-500 hover:text-gray-700"
                              }`}
                            >
                              <span className="text-xs font-medium">
                                Cancel
                              </span>
                            </button>
                          </>
                        ) : (
                          <>
                            <input
                              type="text"
                              value={contactLinkValue}
                              readOnly
                              className={`block w-full pl-10 pr-10 py-2.5 rounded-lg border cursor-not-allowed sm:text-sm ${
                                theme === "dark"
                                  ? "bg-gray-900/50 border-gray-700 text-gray-300"
                                  : "bg-gray-50 border-gray-200 text-gray-700"
                              }`}
                            />
                            <button
                              type="button"
                              onClick={() => setIsEditingContactLink(true)}
                              className={`absolute inset-y-0 right-0 pr-3 flex items-center transition-colors ${
                                theme === "dark"
                                  ? "text-gray-400 hover:text-[#33FF00]"
                                  : "text-gray-500 hover:text-[#2BD400]"
                              }`}
                              title="Edit contact link"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                      {profileErrors.contact_mirsal_link && (
                        <p
                          className={`mt-2 text-sm ${
                            theme === "dark" ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {profileErrors.contact_mirsal_link.message}
                        </p>
                      )}
                      <p
                        className={`mt-2 text-xs ${
                          theme === "dark" ? "text-gray-500" : "text-gray-600"
                        }`}
                      >
                        This link will appear on invitation pages for all guests
                      </p>
                    </div>
                  )}

                  <div className="w-full min-w-0 max-w-full">
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Preferred Language
                    </label>
                    <select
                      className={`block w-full max-w-full min-w-0 box-border rounded-lg border shadow-sm transition-colors py-2.5 px-3 pr-10 text-base sm:text-sm ${
                        theme === "dark"
                          ? "bg-gray-800 border-gray-600 text-white focus:border-[#33FF00] focus:ring-[#33FF00]/20"
                          : "bg-white border-gray-300 text-gray-900 focus:border-[#2BD400] focus:ring-[#2BD400]/20"
                      }`}
                      {...registerProfile("preferred_language")}
                    >
                      <option value="en">English</option>
                      <option value="ar">العربية (Arabic)</option>
                    </select>
                  </div>

                  <div
                    className={`flex justify-end pt-4 border-t ${
                      theme === "dark" ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <Button
                      type="submit"
                      loading={loading}
                      leftIcon={<Save className="h-4 w-4" />}
                      className="min-w-[140px]"
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              )}

              {activeTab === "password" && user?.role !== "guest" && (
                <form
                  onSubmit={handlePasswordSubmit(onPasswordSubmit)}
                  className="space-y-6"
                >
                  <div
                    className={`rounded-xl border p-4 ${
                      theme === "dark"
                        ? "bg-yellow-900/20 border-yellow-700/50"
                        : "bg-yellow-50 border-yellow-200"
                    }`}
                  >
                    <p
                      className={`text-sm ${
                        theme === "dark" ? "text-yellow-300" : "text-yellow-800"
                      }`}
                    >
                      <strong>Security Notice:</strong> Choose a strong password
                      with at least 8 characters, including uppercase,
                      lowercase, and numbers.
                    </p>
                  </div>

                  <div className="relative">
                    <Input
                      label="Current Password"
                      type={showCurrentPassword ? "text" : "password"}
                      leftIcon={<Lock className="h-5 w-5" />}
                      error={passwordErrors.current_password?.message}
                      {...registerPassword("current_password", {
                        required: "Current password is required",
                      })}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        label="New Password"
                        type={showNewPassword ? "text" : "password"}
                        leftIcon={<Lock className="h-5 w-5" />}
                        error={passwordErrors.new_password?.message}
                        {...registerPassword("new_password", {
                          required: "New password is required",
                          minLength: {
                            value: 8,
                            message: "Password must be at least 8 characters",
                          },
                          pattern: {
                            value:
                              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
                            message:
                              "Password must contain at least one uppercase letter, one lowercase letter, and one number",
                          },
                        })}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {newPassword && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-sm ${
                              theme === "dark"
                                ? "text-gray-400"
                                : "text-gray-600"
                            }`}
                          >
                            Password Strength:
                          </span>
                          <span
                            className={`text-sm font-medium ${
                              passwordStrength.score <= 2
                                ? theme === "dark"
                                  ? "text-red-400"
                                  : "text-red-600"
                                : passwordStrength.score <= 3
                                ? theme === "dark"
                                  ? "text-yellow-400"
                                  : "text-yellow-600"
                                : passwordStrength.score <= 4
                                ? theme === "dark"
                                  ? "text-blue-400"
                                  : "text-blue-600"
                                : theme === "dark"
                                ? "text-green-400"
                                : "text-green-600"
                            }`}
                          >
                            {passwordStrength.label}
                          </span>
                        </div>
                        <div
                          className={`w-full rounded-full h-2 ${
                            theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                          }`}
                        >
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                            style={{
                              width: `${(passwordStrength.score / 5) * 100}%`,
                            }}
                          />
                        </div>
                        <div
                          className={`text-xs space-y-1 ${
                            theme === "dark" ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          <div className="grid grid-cols-2 gap-2">
                            <div
                              className={`flex items-center ${
                                newPassword.length >= 8
                                  ? theme === "dark"
                                    ? "text-green-400"
                                    : "text-green-600"
                                  : theme === "dark"
                                  ? "text-gray-500"
                                  : "text-gray-400"
                              }`}
                            >
                              <span className="mr-1">✓</span> 8+ characters
                            </div>
                            <div
                              className={`flex items-center ${
                                /[a-z]/.test(newPassword)
                                  ? theme === "dark"
                                    ? "text-green-400"
                                    : "text-green-600"
                                  : theme === "dark"
                                  ? "text-gray-500"
                                  : "text-gray-400"
                              }`}
                            >
                              <span className="mr-1">✓</span> Lowercase letter
                            </div>
                            <div
                              className={`flex items-center ${
                                /[A-Z]/.test(newPassword)
                                  ? theme === "dark"
                                    ? "text-green-400"
                                    : "text-green-600"
                                  : theme === "dark"
                                  ? "text-gray-500"
                                  : "text-gray-400"
                              }`}
                            >
                              <span className="mr-1">✓</span> Uppercase letter
                            </div>
                            <div
                              className={`flex items-center ${
                                /\d/.test(newPassword)
                                  ? theme === "dark"
                                    ? "text-green-400"
                                    : "text-green-600"
                                  : theme === "dark"
                                  ? "text-gray-500"
                                  : "text-gray-400"
                              }`}
                            >
                              <span className="mr-1">✓</span> Number
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <Input
                      label="Confirm New Password"
                      type={showConfirmPassword ? "text" : "password"}
                      leftIcon={<Lock className="h-5 w-5" />}
                      error={passwordErrors.new_password_confirm?.message}
                      {...registerPassword("new_password_confirm", {
                        required: "Please confirm your new password",
                        validate: (value) =>
                          value === newPassword || "Passwords do not match",
                      })}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  <div
                    className={`flex justify-end pt-4 border-t ${
                      theme === "dark" ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <Button
                      type="submit"
                      loading={loading}
                      leftIcon={<Lock className="h-4 w-4" />}
                      className="min-w-[160px]"
                    >
                      Change Password
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[120]">
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
